import React, { useState, useRef } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Drawer, Button } from 'quantux-ui';
import QuantuxCam from '../quantux-cam';
import { blue, salmon, gray } from '../../theme';
import { padZero } from '../../utils';

const CameraDrawer = (props) => {
  const [showCamErrorHandler, setShowCamErrorHandler] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const cameraRef = useRef();

  const styles = StyleSheet.create({
    drawerContainer: {
      position: 'relative',
      background: blue[900],
      color: blue[200],
      height: '100%',
      display: 'flex',
      flexFlow: 'column'
    },
    drawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0',
      background: blue[700]
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      opacity: showLoader ? '0.3' : '1',
      ':hover': {
        color: !showLoader ? salmon[500] : ''
      }
    },
    drawerContentArea: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 'calc(100vh - 80px)',
      overflowY: 'hidden',
      color: gray[300],
      background: 'black',
      position: 'relative',
      zIndex: '1'
    },
    drawerFooter: {
      height: '65px',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[500],
      borderTop: '3px solid black'
    },
    camErrCloseBtn: {
      color: gray[300],
      position: 'absolute',
      top: '4px',
      right: '4px',
      fontSize: '24px',
      ':hover': {
        color: salmon[500]
      }
    }
  });

  const handleOnClickClose = () => {
    props.onClose();
  };

  const handleOnClickScan = () => {
    if (cameraRef.current) {
      props.onScan(cameraRef.current.takePhoto());
    }
  };

  return (
    <Drawer
      placement='right'
      visible
      toggleDrawer={() => console.log('Not applicable')}
      zIndex="50000"
      showMask={false}
      style={{ border: 'none' }}
      width={window.innerWidth}
    >
      <div className={css(styles.drawerContainer)}>
        {/* Drawer Header */}
        <div className={css(styles.drawerHeader)}>
          <div className='fx fx__hv-center'>
            <div className='ml-2'>
              Document Name
            </div>
          </div>
          <i
            className={`fa fa-lg fa-times-circle ${css(styles.drawerHeaderCloseIcon)}`}
            onClick={handleOnClickClose}
          />
        </div>
        {/* Drawer Container */}
        <div className={css(styles.drawerContentArea)}>
          {showCamErrorHandler &&
            <div
              style={{
                position: 'fixed',
                top: '0',
                left: '0',
                width: '100vw',
                height: '96px',
                background: 'black',
                zIndex: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: '0.90',
                boxShadow: '0px 2px 4px rgba(24, 72, 120, 0.5)'
              }}
            >
              <i
                className={`fa fa-times-circle ${css(styles.camErrCloseBtn)}`}
                onClick={() => {
                  setShowCamErrorHandler(false);
                }}
              />
              <Button
                bntType='primary'
                onClick={() => {
                  setShowCamErrorHandler(false);
                  location.reload();
                }}
              >
                <i className='mr-2 fa fa-refresh' style={{ fontSize: '1.25rem' }}/>
                Reload Page and Try again!
              </Button>
            </div>
          }
          {/* Quantux Camera */}
          <QuantuxCam
            ref={cameraRef}
            facingMode='environment'
            fixError={(msg) => {
              console.log('Quantux Cam error handler!');
              console.log(msg);
              setShowCamErrorHandler(true);
            }}
            errorMessages={{
              noCameraAccessible: 'Camera is not accessible as you have denied permission. Please reload the web page and allow camera access.',
              permissionDenied: 'Permission denied. Please refresh and give camera permission.',
              switchCamera: 'It is not possible to switch camera to different one because there is only one video device accessible.',
              canvas: 'Canvas is not supported.'
            }}
          />
        </div>
        {/* Drawer Footer */}
        <div className={css(styles.drawerFooter)}>
          <Button
            btnType='primary'
            style={{
              minHeight: '48px',
              maxHeight: '48px',
              minWidth: '138px',
              maxWidth: '138px'
            }}
            onClick={handleOnClickScan}
          >
            <div className='fx fx__hv-center'>
              <div style={{ height: '32px', width: '32px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32"><path fill="#fff" fillRule="evenodd" d="M1.286 9.286c.71 0 1.285-.575 1.285-1.286V2.571H8A1.285 1.285 0 1 0 8 0H2.571A2.574 2.574 0 0 0 0 2.571V8c0 .71.575 1.286 1.286 1.286ZM8 29.429H2.571V24A1.285 1.285 0 1 0 0 24v5.429A2.574 2.574 0 0 0 2.571 32H8a1.285 1.285 0 1 0 0-2.571ZM24 0h5.429A2.574 2.574 0 0 1 32 2.571V8a1.285 1.285 0 1 1-2.571 0V2.571H24A1.285 1.285 0 1 1 24 0Zm6.714 22.714c-.71 0-1.285.575-1.285 1.286v5.429H24A1.285 1.285 0 1 0 24 32h5.429A2.574 2.574 0 0 0 32 29.429V24c0-.71-.575-1.286-1.286-1.286ZM17.774 5.78l5.791 5.844c.28.28.436.66.436 1.056v12.49c0 .829-.671 1.5-1.5 1.5h-13c-.83 0-1.5-.671-1.5-1.5V6.834c0-.83.67-1.5 1.5-1.5h7.208c.21 0 .416.046.606.13l.013.005a1.265 1.265 0 0 1 .137.07c.11.066.215.145.308.239Zm-.273 6.058h4.162l-4.162-4.2v4.2Zm5 13.331h-13V6.835H16v5c0 .83.67 1.5 1.5 1.5h5v11.833Z" clipRule="evenodd"/></svg>
              </div>
              <div
                className='ml-2'
                style={{ color: 'white' }}
              >
                {`Scan Pg ${padZero(props.caseDocs.length + 1)}`}
              </div>
            </div>
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

export default CameraDrawer;
